import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Box, Stack} from "@mui/material";
import {Header} from "./header/Header";
import "./main.css"
import {WebSettingsContext} from "../../context/WebSettingsContext";
import {SignInContext} from "../../context/SignInContext";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {ChatDrawer} from "./header/ChatDrawer";
import {StartCallModal} from "./header/StartCallModal";
import {SidebarMenu} from "./SIdebarMenu";
import {ProjectRoles} from "../../context/AppContext";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";
import ProjectJourney from "../../components/tour/ProjectJourney";
import FlowDesignerJourney from "../../components/tour/FlowDesignerJourney";

export const FlowLayout = ({project, flow, node, children, openChat, toggleChat}) => {
  const {settings} = useContext(WebSettingsContext);
  const {currentUser} = useContext(SignInContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isDesignerRoute = /^\/project\/[^/]+\/flow(?:\/|$)/.test(location.pathname);
  const [runProjectJourney, setRunProjectJourney] = useState(() => (
    sessionStorage.getItem("project_journey") === "true"
  ));
  const [projectJourneyRunId, setProjectJourneyRunId] = useState(0);
  const [runFlowDesignerJourney, setRunFlowDesignerJourney] = useState(false);
  const [flowJourneyRunId, setFlowJourneyRunId] = useState(0);
  const previousPathRef = useRef(location.pathname);

  const startProjectJourney = () => {
    if (isDesignerRoute) {
      setFlowJourneyRunId((current) => current + 1);
      setRunFlowDesignerJourney(true);
    } else {
      setProjectJourneyRunId((current) => current + 1);
      setRunProjectJourney(true);
    }
  };

  useEffect(() => {
    if (runProjectJourney) sessionStorage.removeItem("project_journey");
  }, [runProjectJourney]);

  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;
      setRunProjectJourney(false);
      setRunFlowDesignerJourney(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (currentUser !== null) {
      const isAdmin = currentUser?.roles?.includes("admin")

      if (!isAdmin && settings?.underMaintenance) {
        navigate("/under-maintenance");
      }
    }
  }, [settings, currentUser, navigate])

  const mainRef = useRef(null);

  const height = useMemo(() => {
    if (mainRef.current) {
      return `${mainRef.current.offsetHeight}px`;
    }

    return '100%';
  }, [mainRef.current])

  const role = useMemo(() => {
    const isAdmin = currentUser?.roles?.includes("admin");
    if (isAdmin) {
      return ProjectRoles.ADMIN
    }
    const member = project?.members?.find(i => i?.username === currentUser?.username);
    return member?.role || null;
  }, [currentUser, project?.members])

  if (!project?.id) {
    return <LoadingScreen/>
  }

  if (!role){
    return <Navigate to="/"/>
  }

  return (
    <Stack
      direction={"column"}
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        bgcolor: 'transparent'
      }}
    >
      <Header project={project} flow={flow} node={node} openChat={openChat} toggleChat={toggleChat} />
      <Box
        ref={mainRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "row"
        }}
      >
        <SidebarMenu
          project={project} userRole={role} onStartJourney={startProjectJourney}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            maxHeight: height,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <ProjectJourney
            run={runProjectJourney && !!project && !isDesignerRoute}
            pathname={location.pathname}
            userRole={role}
            runId={projectJourneyRunId}
            onFinish={() => setRunProjectJourney(false)}
          />
          <FlowDesignerJourney
            run={(runFlowDesignerJourney || runProjectJourney) && !!project && isDesignerRoute}
            runId={flowJourneyRunId + (runProjectJourney ? 1 : 0)}
            onFinish={() => {
              setRunFlowDesignerJourney(false);
              setRunProjectJourney(false);
            }}
          />
          {children}
        </Box>

        <ChatDrawer
          open={openChat}
          handleClose={toggleChat}
          project={project}
        />
        <StartCallModal project={project} />
      </Box>
    </Stack>
  )
}
